import type {AppStorage} from '../index'
import {
  createFormData,
  isPOST,
  postHeaders,
  toQueryString,
} from '../utils/request.utils'
import axios, {AxiosError, AxiosInstance, AxiosResponse} from 'axios'
import {logAxiosResponse} from 'axios-curlirize'
import {checkNeedRefreshToken} from './refreshTokenLocker'
import type {AuthTokensResponse} from '../models/auth'

const RETRY_UPDATE_TOKEN_MARKER = 'RETRY_MARKER_UPDATE_TOKEN'
const RETRY_UPDATE_TOKEN_LIMIT = 2

const HTTP_STATUS_UNAUTHORIZED = 401

export const simpleResponseConverter = (data: any) => data
export const dataResponseConverter = (data: any) => data.data

/** @deprecated Use v2 api */
export enum Endpoint {
  /** @deprecated Use v2 api */
  createSocialNewUser = 'v1/create_new_user_sotial.php',
  /** @deprecated Use v2 api */
  createNewUser = 'v1/create_new_user.php',
  /** @deprecated Use v2 api */
  checkUser = 'v1/check_user.php',
  /** @deprecated Use v2 api */
  countryList = 'v1/country_list.php',
  /** @deprecated Use v2 api */
  locationByIp = 'v1/get_location_by_gps.php',
  /** @deprecated Use v2 api */
  gpsLocation = 'v1/getGpsByLocation.php',
  /** @deprecated Use v2 api */
  regionList = 'v1/region_list.php',
  /** @deprecated Use v2 api */
  cityList = 'v1/city_list.php',
  /** @deprecated Use v2 api */
  authUser = 'v1/auth_user.php',
  /** @deprecated Use v2 api */
  activeNewUser = 'v1/activate_new_user.php',
  /** @deprecated Use v2 api */
  resendCode = 'v1/send_authsms_user.php',
  /** @deprecated Use v2 api */
  userInfo = 'v1/user.php',
  /** @deprecated Use v2 api */
  remindPassword = 'v1/remind_password.php',
  /** @deprecated Use v2 api */
  logout = 'v1/logout.php',
  /** @deprecated Use v2 api */
  deleteAccount = 'v1/delete_user.php',
  /** @deprecated Use v2 api */
  editUserSettings = 'v1/user_profile_settings.php',
  /** @deprecated Use v2 api */
  getLanguages = 'v1/get_langs.php',
  /** @deprecated Use v2 api */
  userSubscriptions = 'v1/subscriptions.php',
  /** @deprecated Use v2 api */
  subscribePrice = 'v1/subscriptions_price.php',
  /** @deprecated Use v2 api */
  complainAdvertise = 'v1/ads_complain.php',
  /** @deprecated Use v2 api */
  subscribeOnUser = 'v1/user_subscriptions.php',
  /** @deprecated Use v2 api */
  userRatings = 'v1/user_ratings.php',
  /** @deprecated Use v2 api */
  blackList = 'v1/user_profile_settings.php',
  /** @deprecated Use v2 api */
  addFilter = 'v1/search_filters_add.php',
  /** @deprecated Use v2 api */
  deleteFilter = 'v1/search_filters_delete.php',
  /** @deprecated Use v2 api */
  editFilter = 'v1/search_filters_edit.php',
  /** @deprecated Use v2 api */
  fieldOfferValue = 'v1/fields_offer_value.php',
  /** @deprecated Use v2 api */
  coverLink = 'v1/cover_link.php',
  /** @deprecated Use v2 api */
  recognizeWebLink = 'v1/get_adhash_by_url.php',
}

interface SendRequestParams<K> {
  api: Endpoint | string
  body?: K
  method?: 'GET' | 'POST'
  multipart?: boolean
  file?: SendFile
  endpoint?: string
  wrapInQuery?: boolean
}

export interface SendFile {
  file: any
  name: string
}

export class MakeRequest {
  private interceptorAxios: AxiosInstance
  private coreAxios: AxiosInstance

  constructor(
    isLogRequest: boolean,
    private endpoint: string,
    private storage: AppStorage,
    newAuth: {onLogin: () => void},
    sendLog?: (message?: any, ...optionalParams: any[]) => void,
  ) {
    this.interceptorAxios = axios.create()
    this.coreAxios = axios.create()
    if (isLogRequest) {
      logAxiosResponse({
        axiosInstance: this.coreAxios,
        logger: sendLog,
        on401: () => {},
      })
    }
    this.coreAxios.interceptors.request.use((config) => {
      const url = config.url ?? ''
      if (config.method === 'post' || url.includes('v2/chat')) {
        config.headers = config.headers ?? {}
        if (!config.url?.includes('v1') && !config.headers['Content-Type']) {
          config.headers['Content-Type'] = 'application/json'
        }
        const location = this.storage.userLocation
        const language = this.storage.language
        const locationHeader = location
          ? {latitude: location.latitude, longitude: location.longitude}
          : null
        const langHeader = language ? {code: language} : null

        const authorizationToken = storage.authNewToken
        if (authorizationToken)
          config.headers['Authorization'] = `Bearer ${authorizationToken}`
        if (langHeader) config.headers['lang'] = langHeader.code
        if (locationHeader)
          config.headers[
            'location'
          ] = `${locationHeader.latitude};${locationHeader.longitude}`
      }
      return config
    })
    if (newAuth) {
      this.coreAxios.interceptors.response.use(
        (config) => config,
        async (error) => {
          const config = error.config
          const retryCount = config._retryCount ?? 0
          console.log(
            `🔓 Request error: url: ${error.request?.responseURL}, status: ${error.response?.status}`,
          )
          if (error.response?.status !== HTTP_STATUS_UNAUTHORIZED) {
            console.log(`🔓 Request reject (status ${error.response?.status})`)
            return Promise.reject(error)
          }
          if (!this.storage.userHash) {
            console.log('🔓 Request reject (not logged in)')
            return Promise.reject(error)
          }
          if (
            config._retryMarker === RETRY_UPDATE_TOKEN_MARKER &&
            retryCount >= RETRY_UPDATE_TOKEN_LIMIT
          ) {
            console.log('🔓 Request reject -> onLogin (limit exceeded)')
            newAuth?.onLogin()
            return Promise.reject(error)
          }

          const result = await checkNeedRefreshToken(async () => {
            console.log('🔓 >>> PERFORM REFRESHING <<<')
            const refreshToken = storage.authNewRefreshToken
            if (!refreshToken) {
              console.log('🔓 Request reject -> onLogin (no refresh token)')
              newAuth?.onLogin()
              return false
            }
            console.log('🔓 send refreshing', {refreshToken})
            try {
              const response = await this.interceptorAxios.post<
                {data: {token: string}},
                AxiosResponse<AuthTokensResponse>
              >(
                `${this.endpoint}/v2/auth/token/refresh`,
                {data: {token: refreshToken}},
                {headers: {['Content-Type']: 'application/json'}},
              )
              const data = response.data
              console.log('🔓 REFRESH token response', response.status, data)
              storage.saveNewTokens({
                id: data.newAuth.hash,
                accessToken: data.newAuth.access,
                refreshToken: data.newAuth.refresh,
              })
              return true
            } catch (e: any) {
              console.error('⛓ REFRESH TOKEN ERROR', {
                status: e.response?.status,
              })
              return false
            }
          })

          if (!result) {
            console.log('🔓 Request reject -> onLogin. Refresh impossible')
            newAuth?.onLogin()
            return Promise.reject(error)
          }

          config._retryMarker = RETRY_UPDATE_TOKEN_MARKER
          config._retryCount = retryCount + 1
          return this.coreAxios(config)
        },
      )
    }
  }

  private static processError(
    error: AxiosError<any> | Error,
  ): RestResponse<any> {
    let result: {status: number; error: string}

    if ('response' in error && error.response) {
      let message = 'unknownError'
      if (error.response.data) {
        message = Array.isArray(error.response.data)
          ? error.response.data[0]?.code ?? 'unknownError'
          : error.response.data?.code ?? 'unknownError'
      }
      result = {status: error.response.status, error: message}
    } else if ('request' in error && error.request) {
      result = {status: 500, error: error.message ?? 'requestNotReceived'}
    } else {
      result = {status: 500, error: error.message}
    }
    ;(result.status === 401 ? console.info : console.error)(result)
    return result
  }

  private static validateParams(params: NewRequestParams) {
    const page = params.bodyHeaders?.pagination?.page ?? 1
    if (page > 1 && !params.data?.cacheId) {
      throw new Error(
        `cacheId is not present in pagination request, page: ${page} endpoint: ${params.endpoint}`,
      )
    }
  }

  newRequest = async <T>(
    params: NewRequestParams,
    convertor?: (rawData: any) => T,
  ): Promise<RestResponse<T>> => {
    try {
      MakeRequest.validateParams(params)
      const url = `${this.endpoint}/${params.endpoint}`

      let response: AxiosResponse | undefined

      if (params.method === 'GET') {
        response = await this.coreAxios.get(url)
      } else {
        const body: any = params.body ?? {}
        if (params.data) body.data = {...params.data}
        if (params.bodyHeaders) body.headers = {...params.bodyHeaders}
        response = await this.coreAxios(url, {
          method: params.method || 'POST',
          data: body,
          headers: params.headers,
          // @ts-ignore
          curlirize: params.curlirize,
        })
      }

      const result =
        convertor && response?.data ? convertor(response.data) : response?.data
      const headers = response?.data
        ? (response.data as {headers?: {cacheId?: string}}).headers
        : undefined
      if (
        (!params.allowEmptyResponse &&
          response?.status !== 204 &&
          response?.data === undefined) ||
        response?.data === null
      )
        throw new Error('internal_server_error')

      return {
        result,
        headers,
        status: response?.status,
      }
    } catch (error) {
      return MakeRequest.processError(error as Error)
    }
  }

  /** @deprecated use [newRequest] instead */
  request = async <T, K = unknown>(
    params: SendRequestParams<K>,
  ): Promise<RestResponse<T>> => {
    const response = await this.requestData(params)
    return {
      // @ts-ignore
      result: response.result?.data,
      status: response.status,
      error: response.error,
      headers: response.headers,
    }
  }

  /** @deprecated use [newRequest] instead */
  requestData = async <T, K = unknown>({
    api,
    body,
    method,
    multipart = false,
    file,
    wrapInQuery = true,
  }: SendRequestParams<K>): Promise<RestResponse<T>> => {
    try {
      let response: AxiosResponse<any>
      let url = `${this.endpoint}/${api}`

      if (isPOST(method, body)) {
        const jsonData = JSON.stringify(body)
        let bodyData: any = jsonData
        if (wrapInQuery) {
          bodyData =
            file === undefined
              ? `query=${jsonData}`
              : createFormData(this.storage.platform, body, file)
        }

        response = await this.coreAxios.post(url, bodyData, {
          timeout: 10000,
          headers: postHeaders(multipart),
        })
      } else {
        url += toQueryString(body)
        response = await axios.get(url)
      }
      const status = parseInt(response.data.status ?? '500', 10)
      if (status < 200 || status >= 300) {
        return {
          error: response.data.detail ?? 'unknownError',
          status,
        }
      }
      return {
        result: response.data,
        status,
        headers: response.headers,
      }
    } catch (e) {
      return MakeRequest.processError(e as Error)
    }
  }
}

export interface RestResponse<T> {
  readonly error?: string
  readonly message?: string
  readonly result?: T
  readonly status?: number
  readonly headers?: {
    cacheId?: string
    pagination?: {page: number; limit: number; count: number}
  }
}

interface NewRequestParams {
  headers?: Record<string, string>
  bodyHeaders?: Record<string, any>
  /** @deprecated Use body instead */
  data?: any
  body?: any
  endpoint: string
  method?: 'GET' | 'POST' | 'DELETE'
  allowEmptyResponse?: boolean
  readonly curlirize?: boolean
}
